import * as React from 'react';
import { performVerticalAutoScroll, terminateAutoScroll } from './performVerticalAutoScroll';

/**
 * This hook provides event handlers that implement auto-scrolling of an element
 * when the mouse is pressed and moved close to the top and bottom edges of the element.
 */
export function useAutoScroll(scrollbarSize: number = 0) {
    const scrollElement = React.useRef<HTMLElement | undefined>();
    const scrollRect = React.useRef<ClientRect | undefined>();

    const onMouseDown = React.useCallback(
        ({ currentTarget, pageY }: React.MouseEvent<HTMLElement>) => {
            scrollElement.current = currentTarget;
            scrollRect.current = currentTarget.getBoundingClientRect();

            // Do not auto-scroll if mouse down happens in the scrollbar
            const { top, height } = scrollRect.current;
            if (pageY - top > height - scrollbarSize) {
                return;
            }

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);

            performVerticalAutoScroll(scrollElement.current, scrollRect.current!, pageY);
        },
        []
    );

    const onMouseMove = React.useCallback(({ pageY }: MouseEvent) => {
        performVerticalAutoScroll(scrollElement.current!, scrollRect.current!, pageY);
    }, []);

    const onMouseUp = React.useCallback(() => {
        terminateAutoScroll(scrollElement.current!);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
    }, []);

    // During drag-and-drop operations we do not get a mouseup event,
    // so we treat a drop operation as a mouse up.
    return { onMouseDown, onDrop: onMouseUp };
}
